import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  GrowthPlannerHandoffSender,
  IGrowthPlannerHandoffReceipt,
  sendGrowthPlannerHandoff
} from 'containers/Intelligence/handoff/useGrowthPlannerHandoff';

import { OrbitMetricResult, OrbitTurn } from '../types';

/**
 * Why the CTA is not offered, in the vocabulary FR §4.14 states its gates in. Only reasons the
 * reader is permitted to see reach the panel; a blocker the payload does not explain is `notGoverned`
 * rather than a guess.
 */
export type OrbitHandoffBlocker =
  | 'notAnswered'
  | 'notGoverned'
  | 'confidenceBelowMinimum'
  | 'restricted';

export type OrbitHandoffStatus = 'idle' | 'sending' | 'success' | 'blocked' | 'unavailable';

export interface IOrbitHandoffGate {
  /** True only when the payload itself says the handoff may be offered. */
  eligible: boolean;
  /** The metric ids the server cleared for handoff, in payload order. */
  metricIds: string[];
  /** The population behind those metrics, summed over the counts the payload states. */
  customers: number;
  /** At least one band stated no cohort size, so `customers` is a floor, not the whole population. */
  populationPartial: boolean;
  blocker: OrbitHandoffBlocker | null;
}

export interface IOrbitPopulation {
  customers: number;
  partial: boolean;
}

/**
 * The cohort a metric's own payload states. A `null` count is an UNSTATED cohort size, not a cohort
 * of nobody: it is left out of the sum and raises `partial`, where it used to be added as 0 and
 * silently understated the population the handoff was sent with (PLA-9579).
 */
export const populationOf = (result: OrbitMetricResult): IOrbitPopulation => {
  const points = result.series ?? [];
  const stated = points.filter(point => point.n_customers !== null);

  return {
    customers: stated.reduce((total, point) => total + (point.n_customers ?? 0), 0),
    partial: stated.length < points.length
  };
};

/**
 * FR §4.14: "Send to Growth Planner shall appear only for handoff-eligible metrics when the
 * applicable ... gates pass", and "Orbit must not create or display the CTA solely through LLM
 * inference." The dashboard cannot evaluate a subscription, a consent record or a Do-Not-Nudge
 * blocker; `metric_results[].confidence.handoff_ok` is the server's own verdict over all of them,
 * so it is the whole gate and an absent, null or false value HIDES the CTA rather than defaulting
 * it on (PLA-9563 §4a — it used to be drawn on every answered turn that carried any confidence).
 */
export const orbitHandoffGate = (
  turn: Pick<OrbitTurn, 'state' | 'outcome' | 'metric_results'>
): IOrbitHandoffGate => {
  const empty = {
    eligible: false,
    metricIds: [] as string[],
    customers: 0,
    populationPartial: false
  };
  /*
    PLA-9579: read off THIS turn, not off a session-wide flag. One refused request used to lock the
    handoff for every later answer in the conversation; the restriction is a property of the turn
    that carries it, and the turn after it is offered the CTA on its own merits.
  */
  const restricted = turn.outcome === 'permission_restricted';

  if (turn.state !== 'completed' || (turn.outcome !== 'answered' && !restricted)) {
    return { ...empty, blocker: 'notAnswered' };
  }

  const results = Array.isArray(turn.metric_results) ? turn.metric_results : [];
  const governed = results.filter(result => result.confidence !== null);

  if (governed.length === 0) {
    return { ...empty, blocker: 'notGoverned' };
  }

  const cleared = governed.filter(result => result.confidence?.handoff_ok === true);

  if (cleared.length === 0) {
    return { ...empty, blocker: 'confidenceBelowMinimum' };
  }

  /*
    Checked AFTER the metric gates on purpose. A restricted turn is the one blocker the reader is
    permitted to be told about while the metric itself is eligible, so the CTA is still drawn - inert,
    with its reason - instead of vanishing and leaving the restriction unexplained (FR §4.14: "It may
    explain only the blocker information that the user is permitted to see").
  */
  if (restricted) {
    return { ...empty, metricIds: cleared.map(result => result.metric_id), blocker: 'restricted' };
  }

  const population = cleared.reduce<IOrbitPopulation>(
    (total, result) => {
      const stated = populationOf(result);

      return {
        customers: total.customers + stated.customers,
        partial: total.partial || stated.partial
      };
    },
    { customers: 0, partial: false }
  );

  return {
    eligible: true,
    metricIds: cleared.map(result => result.metric_id),
    customers: population.customers,
    populationPartial: population.partial,
    blocker: null
  };
};

export interface IOrbitGrowthPlannerHandoff {
  gate: IOrbitHandoffGate;
  status: OrbitHandoffStatus;
  receipt: IGrowthPlannerHandoffReceipt | null;
  errorRef: string | null;
  send: () => void;
}

export interface IOrbitGrowthPlannerHandoffOptions {
  turn: Pick<OrbitTurn, 'state' | 'outcome' | 'metric_results' | 'context'>;
  sender?: GrowthPlannerHandoffSender;
}

/**
 * The Growth Planner handoff from an Orbit answer.
 *
 * It reuses `sendGrowthPlannerHandoff` — the one seam the Intelligence screens send through —
 * rather than forking a second one, so the day a real endpoint lands both surfaces get it from the
 * same change. `useGrowthPlannerHandoff` itself is NOT reused: that hook resolves its metrics
 * through `useCardData(cardKey)`, and an Orbit turn has no card, only the governed metric results
 * the answer was computed from.
 *
 * TODO(AF-1): real outcomes need the backend handoff endpoint. Until PLA-8839 exposes one, `send`
 * resolves a local receipt, so `success` here means "the request was formed and accepted locally",
 * never "Growth Planner has it". The blocked / restricted / unavailable branches are real: they are
 * decided by the payload's own governance fields before anything is sent.
 */
export const useOrbitGrowthPlannerHandoff = ({
  turn,
  sender = sendGrowthPlannerHandoff
}: IOrbitGrowthPlannerHandoffOptions): IOrbitGrowthPlannerHandoff => {
  const gate = useMemo(() => orbitHandoffGate(turn), [turn]);
  const [status, setStatus] = useState<OrbitHandoffStatus>('idle');
  const [receipt, setReceipt] = useState<IGrowthPlannerHandoffReceipt | null>(null);
  const [errorRef, setErrorRef] = useState<string | null>(null);

  // The panel can be closed, or the transcript cleared, while the seam is in flight.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const send = useCallback(() => {
    // FR §4.14: "the current authorization and handoff gates shall be re-evaluated" on selection.
    const current = orbitHandoffGate(turn);

    if (!current.eligible) {
      setStatus('blocked');

      return;
    }

    setStatus('sending');
    setErrorRef(null);

    sender({
      // Only the minimum governed context the handoff contract needs: which metrics, and how many
      // customers stand behind them. No question text, no answer prose, no customer identity.
      cardKey: turn.context.screen ?? '',
      metricKeys: current.metricIds,
      customers: current.customers
    })
      .then(sent => {
        if (!mounted.current) {
          return;
        }

        setReceipt(sent);
        setStatus('success');
      })
      .catch((error: unknown) => {
        if (!mounted.current) {
          return;
        }

        setErrorRef(error instanceof Error && error.message ? error.message : 'GP-UNKNOWN');
        setStatus('unavailable');
      });
  }, [sender, turn]);

  return { gate, status, receipt, errorRef, send };
};

export default useOrbitGrowthPlannerHandoff;
